import { useEffect, useState } from 'react';
import { listLeads, updateLeadStatus, deleteLead, leadsStats } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { Trash2, RefreshCw, ExternalLink } from 'lucide-react';

const statusStyles = {
  new: 'bg-[#1D4ED8] text-white',
  contacted: 'bg-[#0A0A0A]/10 text-[#0A0A0A]',
  won: 'bg-emerald-600 text-white',
  lost: 'bg-[#DC2626] text-white',
};

const fmt = (iso) => new Date(iso).toLocaleString();

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([listLeads(), leadsStats()]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const setStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      toast.success(`Marked as ${status}`);
      refresh();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await deleteLead(id);
      toast.success('Lead deleted');
      refresh();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid={ADMIN.page} className="min-h-screen bg-[#FAFAF5] pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <div className="font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#1D4ED8] mb-4">// Admin dashboard</div>
            <h1 className="font-display uppercase text-[#0A0A0A] text-4xl md:text-6xl leading-[1.02] tracking-tight">Leads</h1>
          </div>
          <button onClick={refresh} className="inline-flex items-center gap-3 border border-[#0A0A0A]/15 text-[#0A0A0A] px-5 py-3 font-mono-label text-[11px] tracking-[0.22em] uppercase hover:bg-[#0A0A0A]/5">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
          <Stat testid={ADMIN.statTotal} label="Total leads" value={stats.total} accent />
          <Stat testid={ADMIN.statNew} label="New" value={stats.new} />
          <Stat testid={ADMIN.statContacted} label="Contacted" value={stats.contacted} />
          <Stat testid={ADMIN.statWon} label="Won" value={stats.won} highlight />
        </div>

        <div className="border border-[#0A0A0A]/10 overflow-x-auto bg-white">
          <table data-testid={ADMIN.table} className="w-full min-w-[900px] text-left">
            <thead className="font-mono-label text-[10px] tracking-[0.24em] uppercase text-[#0A0A0A]/50 border-b border-[#0A0A0A]/10">
              <tr>
                <th className="p-4">When</th>
                <th className="p-4">Name</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Service</th>
                <th className="p-4">Source</th>
                <th className="p-4">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-10 text-center text-[#0A0A0A]/50">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-[#0A0A0A]/50">No leads yet. Submissions from the site show up here.</td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} data-testid={ADMIN.row(l.id)} className="border-b border-[#0A0A0A]/5 hover:bg-[#0A0A0A]/[0.02] align-top">
                  <td className="p-4 text-[#0A0A0A]/60 text-sm">{fmt(l.created_at)}</td>
                  <td className="p-4 text-[#0A0A0A] font-medium">{l.name}</td>
                  <td className="p-4 text-[#0A0A0A]/75 text-sm">
                    <a href={`tel:${l.phone}`} className="block hover:text-[#1D4ED8]">{l.phone}</a>
                    <a href={`mailto:${l.email}`} className="block hover:text-[#1D4ED8] break-all">{l.email}</a>
                    {l.message && <div className="mt-2 text-[#0A0A0A]/60 text-xs max-w-xs whitespace-pre-wrap">{l.message}</div>}
                  </td>
                  <td className="p-4 text-[#0A0A0A]/75 text-sm">{l.service || '—'}</td>
                  <td className="p-4 text-[#0A0A0A]/55 text-xs font-mono-label uppercase tracking-widest">{l.source || '—'}</td>
                  <td className="p-4">
                    <select
                      data-testid={ADMIN.statusSelect(l.id)}
                      value={l.status}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      className={`text-xs font-mono-label tracking-[0.22em] uppercase px-3 py-1.5 border-0 outline-none ${statusStyles[l.status] || 'bg-[#0A0A0A]/10 text-[#0A0A0A]'}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="won">Won</option>
                      <option value="lost">Lost</option>
                    </select>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <a href={`mailto:${l.email}`} className="p-2 border border-[#0A0A0A]/10 text-[#0A0A0A]/60 hover:text-[#1D4ED8]" aria-label="Email"><ExternalLink className="w-3.5 h-3.5" /></a>
                      <button data-testid={ADMIN.delete(l.id)} onClick={() => remove(l.id)} className="p-2 border border-[#0A0A0A]/10 text-[#0A0A0A]/60 hover:text-[#DC2626]" aria-label="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, highlight, testid }) {
  const base = 'border p-6';
  const style = accent
    ? 'bg-[#1D4ED8] border-[#1D4ED8] text-white'
    : highlight
      ? 'bg-white border-[#DC2626] text-[#0A0A0A]'
      : 'bg-white border-[#0A0A0A]/10 text-[#0A0A0A]';
  return (
    <div data-testid={testid} className={`${base} ${style}`}>
      <div className={`font-mono-label text-[10px] tracking-[0.24em] uppercase ${accent ? 'text-white/70' : 'text-[#0A0A0A]/50'} mb-3`}>{label}</div>
      <div className="font-display text-4xl md:text-5xl">{value}</div>
    </div>
  );
}
